import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { beginLogin, beginLogout, completeLoginIfCallback, isAuthConfigured, refreshTokens } from './oidc';

const REDIRECT_GUARD_KEY = 'oidc:redirecting';

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  isRedirecting: boolean;
  error: string | null;
}

interface AuthContextValue extends AuthState {
  login: () => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ isAuthenticated: false, isLoading: true, isRedirecting: false, error: null });

  useEffect(() => {
    async function bootstrap() {
      try {
        // Handle the redirect back from Keycloak (exchanges ?code for tokens).
        if (await completeLoginIfCallback()) {
          sessionStorage.removeItem(REDIRECT_GUARD_KEY);
          setState({ isAuthenticated: true, isLoading: false, isRedirecting: false, error: null });
          return;
        }

        // A stored-but-expired session can still be renewed silently (refresh-token
        // grant). refreshTokens() resolves false when there is no renewable session.
        if (await refreshTokens()) {
          sessionStorage.removeItem(REDIRECT_GUARD_KEY);
          setState({ isAuthenticated: true, isLoading: false, isRedirecting: false, error: null });
          return;
        }

        // Not authenticated — redirect to IDP or surface an error.
        if (!isAuthConfigured()) {
          setState({ isAuthenticated: false, isLoading: false, isRedirecting: false, error: 'Auth is not configured. Check VITE_KEYCLOAK_AUTHORITY and VITE_KEYCLOAK_CLIENT_ID.' });
          return;
        }
        if (sessionStorage.getItem(REDIRECT_GUARD_KEY)) {
          setState({ isAuthenticated: false, isLoading: false, isRedirecting: false, error: 'Sign-in failed or was cancelled. Please try again.' });
          return;
        }
        sessionStorage.setItem(REDIRECT_GUARD_KEY, '1');
        setState({ isAuthenticated: false, isLoading: false, isRedirecting: true, error: null });
        void beginLogin();
      } catch (err) {
        console.error('[auth] bootstrap error', err);
        setState({ isAuthenticated: false, isLoading: false, isRedirecting: false, error: 'An unexpected error occurred during sign-in.' });
      }
    }

    bootstrap();
  }, []);

  const login = useCallback(() => {
    sessionStorage.removeItem(REDIRECT_GUARD_KEY);
    void beginLogin();
  }, []);

  const logout = useCallback(() => {
    void beginLogout();
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
